import { Skeleton, SkeletonRegion } from '#/components/ui/Skeleton';
import { cn } from '#/utils/cn';

interface DataTableSkeletonProps {
  /** Number of columns to stand in for. */
  columns: number;
  /**
   * Number of body rows. Pass the query's `limit` — the count the response
   * will actually carry — so the card doesn't resize when rows land.
   */
  rows?: number;
  /** Matches DataTable's tighter cell padding and type scale. */
  compact?: boolean;
  /**
   * The card heading the table sits under. It never depends on the response,
   * so it is shown for real rather than as a bar.
   */
  title?: string;
  /** Announced while the table loads, e.g. "Loading top equities…". */
  label: string;
  className?: string;
}

/**
 * Placeholder for a `DataTable` that has no data yet, on the same header rule,
 * row dividers and cell padding so the real table drops straight in.
 *
 * Only for the first load. Refetches keep the previous rows on screen (see the
 * explore tables' `keepPreviousData` + overlay treatment) — swapping them for
 * bones would be a step backwards.
 */
export function DataTableSkeleton({
  columns,
  rows = 10,
  compact = false,
  title,
  label,
  className,
}: DataTableSkeletonProps) {
  const cellClass = compact ? 'px-8 py-12' : 'p-12 first:ps-16 last:pe-16';
  const rowBarClass = compact ? 'h-18' : 'h-28';

  return (
    <SkeletonRegion label={label} className={className}>
      {title && (
        <div
          className={cn(
            'flex flex-wrap items-start justify-between gap-x-12 gap-y-8',
            compact ? 'mb-12' : 'mb-20',
          )}
        >
          <h3 className="text-fluid-base text-fg-header min-w-0 flex-1 font-semibold">
            <span className="text-balance">{title}</span>
          </h3>
          <Skeleton className="h-20 w-160" />
        </div>
      )}
      <div className="overflow-x-hidden">
        <table
          aria-hidden="true"
          className={cn(
            'w-full border-collapse',
            compact ? 'text-static-12 min-w-0 table-fixed' : 'min-w-lg',
          )}
        >
          <tbody>
            <tr className="border-divider-subtle border-b">
              {Array.from({ length: columns }, (_, column) => (
                <td key={column} className={cellClass}>
                  <Skeleton className={rowBarClass} />
                </td>
              ))}
            </tr>
            {Array.from({ length: rows }, (_row, row) => (
              <tr key={row} className="border-divider-subtle border-t">
                {Array.from({ length: columns }, (_cell, column) => (
                  <td key={column} className={cellClass}>
                    <Skeleton className={rowBarClass} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SkeletonRegion>
  );
}
